import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Loader2, User, Dumbbell, Shield } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Terms() {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await axios.get(`${API_URL}/terms`);
        setTerms(response.data);
      } catch (error) {
        console.error('Failed to fetch terms:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="terms-page">
      <Header />
      
      <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-8 animate-fade-in-up">
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none uppercase text-white mb-3">
              Terms & <span className="text-primary">Conditions</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Please read these terms carefully before using TRAINR
            </p>
          </div>

          {/* Terms Content */}
          <Tabs defaultValue="user" className="animate-fade-in-up animation-delay-100">
            <TabsList className="bg-[#141414] border border-white/5 mb-6 w-full justify-start">
              <TabsTrigger 
                value="user" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                User Terms
              </TabsTrigger>
              <TabsTrigger 
                value="trainer" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white flex items-center gap-2"
              >
                <Dumbbell className="w-4 h-4" />
                Trainer Terms
              </TabsTrigger>
              <TabsTrigger 
                value="liability" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Liability
              </TabsTrigger>
            </TabsList>

            {/* User Terms */}
            <TabsContent value="user">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-6 border-b border-white/5">
                  <h2 className="font-heading text-2xl font-bold uppercase text-white">
                    {terms?.user_terms?.title || 'User Terms & Conditions'}
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  {terms?.user_terms?.sections?.map((section, index) => (
                    <div key={index}>
                      <h3 className="text-lg font-semibold text-white mb-2">{section.title}</h3>
                      <p className="text-gray-400 leading-relaxed">{section.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Trainer Terms */}
            <TabsContent value="trainer">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-6 border-b border-white/5">
                  <h2 className="font-heading text-2xl font-bold uppercase text-white">
                    {terms?.trainer_terms?.title || 'Trainer Terms & Conditions'}
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  {terms?.trainer_terms?.sections?.map((section, index) => (
                    <div key={index}>
                      <h3 className="text-lg font-semibold text-white mb-2">{section.title}</h3>
                      <p className="text-gray-400 leading-relaxed">{section.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Liability Clauses */}
            <TabsContent value="liability">
              <div className="bg-[#141414] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-6 border-b border-white/5">
                  <h2 className="font-heading text-2xl font-bold uppercase text-white">
                    Liability Limitations
                  </h2>
                </div>
                <div className="p-6">
                  <ul className="space-y-4">
                    {terms?.liability_clauses?.map((clause, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <p className="text-gray-400 leading-relaxed">{clause}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Summary Box */}
          <div className="mt-8 bg-[#1A1A1A] rounded-2xl p-6 border border-primary/20 animate-fade-in-up animation-delay-200">
            <h3 className="font-semibold text-white mb-4">Key Points Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <p className="text-primary font-medium">For Users:</p>
                <ul className="text-gray-400 space-y-1">
                  <li>- 50% refund for user no-shows</li>
                  <li>- 100% refund if trainer misses</li>
                  <li>- 2-hour dispute window after session</li>
                  <li>- Full refund if cancelled 24+ hours before</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-primary font-medium">For Trainers:</p>
                <ul className="text-gray-400 space-y-1">
                  <li>- 15% platform commission (10% premium)</li>
                  <li>- Must provide payout info to receive funds</li>
                  <li>- 100% refund if you miss a session</li>
                  <li>- Mark sessions completed in dashboard</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 mb-4">Ready to get started?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <Button className="bg-primary text-white hover:bg-primary/90 px-8">
                  Sign Up as User
                </Button>
              </Link>
              <Link to="/become-a-trainer">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white px-8">
                  Become a Trainer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
